import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import PhotosFilterBar from './PhotosFilterBar'
import PhotoTimeline from './PhotoTimeline'
import { EMPTY_PHOTOS_FILTERS, PhotosFilters, toPhotoFilterInput } from '../lib/filters'
import { photosToAdd } from '../lib/picker'
import type { Photo } from '../types'

interface Props {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (photos: Photo[]) => Promise<void> | void
  excludeFileIds?: string[]
}

/** Pick existing gallery photos instead of uploading again (photos spec §6.3). */
const PhotoPickerDialog: React.FC<Props> = ({ projectId, open, onOpenChange, onPick, excludeFileIds = [] }) => {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<PhotosFilters>(EMPTY_PHOTOS_FILTERS)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loaded, setLoaded] = useState<Photo[]>([])
  const [busy, setBusy] = useState(false)
  const filter = useMemo(() => toPhotoFilterInput(filters, { folderId: null, includeSubfolders: false, albumId: null }), [filters])
  const picked = photosToAdd(loaded, selectedIds, excludeFileIds)

  // a cancelled or dismissed pick must not come back on the next open
  useEffect(() => setSelectedIds(new Set()), [open])

  const toggle = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedIds(next)
  }

  const confirm = async () => {
    setBusy(true)
    try {
      await onPick(picked)
      onOpenChange(false)
    } catch (e) {
      // the caller reports the failure; keep the dialog open so the selection survives
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t('Photos_picker_title')}</DialogTitle>
        </DialogHeader>
        <PhotosFilterBar projectId={projectId} filters={filters} onFiltersChange={setFilters} />
        {open && (
          <PhotoTimeline
            projectId={projectId}
            filter={filter}
            size="S"
            className="h-[55vh]"
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onPhotosChange={setLoaded}
            onOpen={(id) => toggle(id)}
          />
        )}
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>{t('Common_cancel')}</Button>
          <Button disabled={busy || picked.length === 0} onClick={confirm}>
            {busy && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Photos_picker_add', { count: picked.length })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PhotoPickerDialog
